import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Chip,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Assignment as AssignmentIcon,
  Psychology as PsychologyIcon,
  Info as InfoIcon,
  Grade as GradeIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Block as BlockIcon,
  PersonOff as PersonOffIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  BrokenImage as BrokenImageIcon,
  HelpOutline as HelpOutlineIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { AssessedSession } from "../../../redux/actions/assessmentActions";

interface AssessedSessionMemoryViewProps {
  currentAssessedSession: AssessedSession;
}

const AssessedSessionMemoryView: React.FC<AssessedSessionMemoryViewProps> = ({
  currentAssessedSession,
}) => {
  const analysisResults = currentAssessedSession.analysis_results;

  return (
    <Grid container spacing={3}>
      {/* Analysis Results */}
      {currentAssessedSession.state === "ready" && analysisResults ? (
        <>
          {/* Spam Assessment */}
          {analysisResults.assessed_spam &&
            analysisResults.assessed_spam.label &&
            analysisResults.assessed_spam.label !== "None" && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Spam Assessment
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {(() => {
                    const label = analysisResults.assessed_spam.label;
                    let icon, severity, title;

                    switch (label) {
                      case "NoEngagement":
                        icon = <PersonOffIcon sx={{ mr: 1 }} />;
                        severity = "warning";
                        title = "No Engagement";
                        break;
                      case "OffTopicConversation":
                        icon = <ChatBubbleOutlineIcon sx={{ mr: 1 }} />;
                        severity = "info";
                        title = "Off-Topic Conversation";
                        break;
                      case "IncoherentTranscript":
                        icon = <BrokenImageIcon sx={{ mr: 1 }} />;
                        severity = "error";
                        title = "Incoherent Transcript";
                        break;
                      case "Other":
                        icon = <HelpOutlineIcon sx={{ mr: 1 }} />;
                        severity = "info";
                        title = "Other";
                        break;
                      default:
                        icon = <BlockIcon sx={{ mr: 1 }} />;
                        severity = "error";
                        title = label;
                    }

                    return (
                      <Alert
                        severity={severity as any}
                        sx={{ mb: 2 }}
                        icon={icon}
                      >
                        <Typography
                          variant="subtitle1"
                          fontWeight="medium"
                          gutterBottom
                        >
                          {title}
                        </Typography>
                        <Typography variant="body2">
                          {analysisResults.assessed_spam.reason}
                        </Typography>
                      </Alert>
                    );
                  })()}
                </Paper>
              </Grid>
            )}

          {/* Summary Data (if no detailed assessment available) */}
          {analysisResults.summary &&
            Object.keys(analysisResults.summary).length > 0 &&
            !analysisResults.overall_assessment &&
            !analysisResults.question_analyses && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Assessment Summary
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {/* Title */}
                  {analysisResults.summary.title && (
                    <Box sx={{ mb: 4, textAlign: "center" }}>
                      <Typography
                        variant="h4"
                        fontWeight="600"
                        color="text.primary"
                        sx={{
                          mb: 1,
                          letterSpacing: "-0.5px",
                          lineHeight: 1.3,
                        }}
                      >
                        {analysisResults.summary.title}
                      </Typography>
                      <Divider
                        sx={{
                          width: "80px",
                          mx: "auto",
                          borderWidth: 2,
                          borderColor: "primary.main",
                        }}
                      />
                    </Box>
                  )}

                  {/* Overall Score */}
                  {analysisResults.summary.overall_score !== undefined && (
                    <Card
                      variant="outlined"
                      sx={{
                        mb: 3,
                        bgcolor: "background.default",
                        borderWidth: 2,
                        borderColor:
                          analysisResults.summary.overall_score >= 4
                            ? "success.main"
                            : analysisResults.summary.overall_score <= 2
                            ? "error.main"
                            : "primary.main",
                      }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={3}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: "primary.light",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <GradeIcon
                              sx={{ fontSize: 40, color: "primary.main" }}
                            />
                          </Box>
                          <Box flex={1}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Overall Score
                            </Typography>
                            <Typography
                              variant="h3"
                              color="primary"
                              fontWeight="bold"
                            >
                              {analysisResults.summary.overall_score}
                              <Typography
                                component="span"
                                variant="h5"
                                color="text.secondary"
                              >
                                /5
                              </Typography>
                            </Typography>
                          </Box>
                          {analysisResults.summary.overall_score >= 4 ? (
                            <TrendingUpIcon
                              sx={{ fontSize: 48, color: "success.main" }}
                            />
                          ) : analysisResults.summary.overall_score <= 2 ? (
                            <TrendingDownIcon
                              sx={{ fontSize: 48, color: "error.main" }}
                            />
                          ) : null}
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Recommendation */}
                  {analysisResults.summary.recommendation && (
                    <Card variant="outlined" sx={{ mb: 3, p: 3 }}>
                      <Typography
                        variant="h6"
                        fontWeight="medium"
                        gutterBottom
                        color={
                          analysisResults.summary.recommendation ===
                            "Thumbs up" ||
                          analysisResults.summary.recommendation.startsWith(
                            "hire"
                          )
                            ? "success.main"
                            : analysisResults.summary.recommendation ===
                                "Thumbs down" ||
                              analysisResults.summary.recommendation.startsWith(
                                "do not hire"
                              )
                            ? "error.main"
                            : "warning.main"
                        }
                      >
                        Recommendation
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                        {analysisResults.summary.recommendation}
                      </Typography>
                    </Card>
                  )}

                  {/* Summary Text */}
                  {analysisResults.summary.summary && (
                    <Card
                      variant="outlined"
                      sx={{ mb: 3, p: 3, bgcolor: "grey.50" }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="medium"
                        gutterBottom
                        color="primary"
                      >
                        Summary
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                        {analysisResults.summary.summary}
                      </Typography>
                    </Card>
                  )}

                  {/* Key Facts */}
                  {analysisResults.summary.key_facts && (
                    <Card variant="outlined" sx={{ mb: 3, p: 3 }}>
                      <Typography
                        variant="h6"
                        fontWeight="medium"
                        gutterBottom
                        color="primary"
                      >
                        Key Facts
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          lineHeight: 1.8,
                          whiteSpace: "pre-wrap",
                          fontFamily: "monospace",
                          fontSize: "0.9rem",
                        }}
                      >
                        {analysisResults.summary.key_facts}
                      </Typography>
                    </Card>
                  )}

                  {/* Overall Summary */}
                  {analysisResults.summary.overall_summary && (
                    <Card
                      variant="outlined"
                      sx={{ mb: 3, p: 3, bgcolor: "grey.50" }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="medium"
                        gutterBottom
                        color="primary"
                      >
                        Overall Summary
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                        {analysisResults.summary.overall_summary}
                      </Typography>
                    </Card>
                  )}
                </Paper>
              </Grid>
            )}

          {/* Overall Assessment Summary */}
          {analysisResults.overall_assessment && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Overall Assessment
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {/* Overall Score */}
                {analysisResults.overall_assessment.overall_score !==
                  undefined && (
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 3,
                      bgcolor: "background.default",
                      borderWidth: 2,
                      borderColor:
                        analysisResults.overall_assessment.overall_score >= 4
                          ? "success.main"
                          : analysisResults.overall_assessment
                              .overall_score <= 2
                          ? "error.main"
                          : "primary.main",
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={3}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "primary.light",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <GradeIcon
                            sx={{ fontSize: 40, color: "primary.main" }}
                          />
                        </Box>
                        <Box flex={1}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                          >
                            Overall Score
                          </Typography>
                          <Typography
                            variant="h3"
                            color="primary"
                            fontWeight="bold"
                          >
                            {analysisResults.overall_assessment.overall_score}
                            <Typography
                              component="span"
                              variant="h5"
                              color="text.secondary"
                            >
                              /5
                            </Typography>
                          </Typography>
                        </Box>
                        {analysisResults.overall_assessment.overall_score >=
                        4 ? (
                          <TrendingUpIcon
                            sx={{ fontSize: 48, color: "success.main" }}
                          />
                        ) : analysisResults.overall_assessment
                            .overall_score <= 2 ? (
                          <TrendingDownIcon
                            sx={{ fontSize: 48, color: "error.main" }}
                          />
                        ) : null}
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Summary */}
                {analysisResults.overall_assessment.summary && (
                  <Card
                    variant="outlined"
                    sx={{ mb: 3, p: 3, bgcolor: "grey.50" }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="medium"
                      gutterBottom
                      color="primary"
                    >
                      Summary
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      {analysisResults.overall_assessment.summary}
                    </Typography>
                  </Card>
                )}

                {/* Strengths and Weaknesses */}
                <Grid container spacing={3}>
                  {analysisResults.overall_assessment.strengths &&
                    analysisResults.overall_assessment.strengths.length >
                      0 && (
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: "100%" }}>
                          <CardContent>
                            <Typography
                              variant="subtitle1"
                              fontWeight="medium"
                              color="success.main"
                              gutterBottom
                            >
                              Strengths
                            </Typography>
                            <List dense>
                              {analysisResults.overall_assessment.strengths.map(
                                (strength: string, index: number) => (
                                  <ListItem key={index}>
                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                      <CheckCircleIcon
                                        fontSize="small"
                                        color="success"
                                      />
                                    </ListItemIcon>
                                    <ListItemText primary={strength} />
                                  </ListItem>
                                )
                              )}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                  {analysisResults.overall_assessment.areas_for_improvement &&
                    analysisResults.overall_assessment.areas_for_improvement
                      .length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: "100%" }}>
                          <CardContent>
                            <Typography
                              variant="subtitle1"
                              fontWeight="medium"
                              color="warning.main"
                              gutterBottom
                            >
                              Areas for Improvement
                            </Typography>
                            <List dense>
                              {analysisResults.overall_assessment.areas_for_improvement.map(
                                (area: string, index: number) => (
                                  <ListItem key={index}>
                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                      <InfoIcon
                                        fontSize="small"
                                        color="warning"
                                      />
                                    </ListItemIcon>
                                    <ListItemText primary={area} />
                                  </ListItem>
                                )
                              )}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Assessed Questions - From the JSON structure */}
          {analysisResults.assessed_questions &&
            typeof analysisResults.assessed_questions === "object" &&
            Object.keys(analysisResults.assessed_questions).length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Assessed Questions
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {Object.entries(analysisResults.assessed_questions).map(
                    (
                      [questionId, assessedQuestion]: [string, any],
                      index: number
                    ) => {
                      const question = assessedQuestion.question;
                      const score = assessedQuestion.score;
                      // Handle both old field name (answer_summary) and new field name (candidate_raw_answer)
                      const answerSummary =
                        assessedQuestion.answer_summary ||
                        assessedQuestion.candidate_raw_answer;
                      const scoreReasoning = assessedQuestion.score_reasoning;

                      // Format question ID for display
                      const formatQuestionId = (id: string) => {
                        return id
                          .split("_")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(" ");
                      };

                      return (
                        <Accordion key={questionId} sx={{ mb: 2 }}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              bgcolor:
                                score >= 4
                                  ? "success.50"
                                  : score <= 2
                                  ? "error.50"
                                  : "grey.50",
                              "&:hover": {
                                bgcolor:
                                  score >= 4
                                    ? "success.100"
                                    : score <= 2
                                    ? "error.100"
                                    : "grey.100",
                              },
                              borderLeft: 4,
                              borderColor:
                                score >= 4
                                  ? "success.main"
                                  : score <= 2
                                  ? "error.main"
                                  : "primary.main",
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={2}
                              width="100%"
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <QuestionAnswerIcon fontSize="small" />
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                >
                                  {formatQuestionId(questionId)}
                                </Typography>
                              </Box>
                              {score !== undefined && (
                                <Chip
                                  label={`Score: ${score}/5`}
                                  size="small"
                                  variant="outlined"
                                  color={
                                    score >= 4
                                      ? "success"
                                      : score <= 2
                                      ? "error"
                                      : "default"
                                  }
                                />
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {/* Question Text */}
                            <Card
                              variant="outlined"
                              sx={{ mb: 3, p: 2, bgcolor: "primary.50" }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="primary.main"
                                fontWeight="bold"
                                gutterBottom
                              >
                                QUESTION
                              </Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {question || "No question text available"}
                              </Typography>
                            </Card>

                            {/* Answer Summary / Candidate Raw Answer */}
                            {answerSummary && (
                              <Box mb={3}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="medium"
                                  gutterBottom
                                  color="text.primary"
                                >
                                  {assessedQuestion.candidate_raw_answer
                                    ? "Candidate's Answer"
                                    : "Answer Summary"}
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      lineHeight: 1.6,
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {answerSummary}
                                  </Typography>
                                </Paper>
                              </Box>
                            )}

                            {/* Score Reasoning */}
                            {scoreReasoning && (
                              <Box mb={3}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="medium"
                                  gutterBottom
                                  color="text.primary"
                                >
                                  Score Reasoning
                                </Typography>
                                <Paper
                                  sx={{
                                    p: 2,
                                    bgcolor:
                                      score >= 4
                                        ? "success.50"
                                        : score <= 2
                                        ? "error.50"
                                        : "grey.50",
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    sx={{ lineHeight: 1.6 }}
                                  >
                                    {scoreReasoning}
                                  </Typography>
                                </Paper>
                              </Box>
                            )}

                            {/* Updated At */}
                            {assessedQuestion.updated_at && (
                              <Box mt={2}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Updated:{" "}
                                  {new Date(
                                    assessedQuestion.updated_at
                                  ).toLocaleString()}
                                </Typography>
                              </Box>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    }
                  )}
                </Paper>
              </Grid>
            )}

          {/* Question-by-Question Analysis - Legacy format */}
          {analysisResults.question_analyses &&
            Array.isArray(analysisResults.question_analyses) &&
            analysisResults.question_analyses.length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Question-by-Question Analysis
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  {analysisResults.question_analyses.map(
                    (questionAnalysis: any, index: number) => {
                      const getQuestionTypeIcon = () => {
                        switch (questionAnalysis?.question_type) {
                          case "informational":
                            return <InfoIcon fontSize="small" />;
                          case "general":
                            return <AssignmentIcon fontSize="small" />;
                          case "behavioral":
                            return <PsychologyIcon fontSize="small" />;
                          case "background":
                            return <BusinessIcon fontSize="small" />;
                          default:
                            return <QuestionAnswerIcon fontSize="small" />;
                        }
                      };

                      const getQuestionTypeColor = () => {
                        switch (questionAnalysis?.question_type) {
                          case "informational":
                            return "info";
                          case "general":
                            return "primary";
                          case "behavioral":
                            return "secondary";
                          case "background":
                            return "info";
                          default:
                            return "default";
                        }
                      };

                      return (
                        <Accordion key={index} sx={{ mb: 2 }}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              bgcolor: "grey.50",
                              "&:hover": { bgcolor: "grey.100" },
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={2}
                              width="100%"
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                {getQuestionTypeIcon()}
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                >
                                  Question {index + 1}
                                </Typography>
                              </Box>
                              {questionAnalysis?.question_type && (
                                <Chip
                                  label={questionAnalysis.question_type}
                                  size="small"
                                  color={getQuestionTypeColor() as any}
                                />
                              )}
                              {questionAnalysis?.score !== undefined && (
                                <Chip
                                  label={`Score: ${questionAnalysis.score}/5`}
                                  size="small"
                                  variant="outlined"
                                  color={
                                    questionAnalysis.score >= 4
                                      ? "success"
                                      : questionAnalysis.score <= 2
                                      ? "error"
                                      : "default"
                                  }
                                />
                              )}
                              <Box flexGrow={1} />
                              {questionAnalysis?.answered !== undefined && (
                                <Typography
                                  variant="caption"
                                  color={
                                    questionAnalysis.answered
                                      ? "success.main"
                                      : "text.secondary"
                                  }
                                >
                                  {questionAnalysis.answered
                                    ? "Answered"
                                    : "Not Answered"}
                                </Typography>
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {/* Question Text */}
                            {questionAnalysis?.question_text && (
                              <Box mb={3}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  gutterBottom
                                >
                                  Question:
                                </Typography>
                                <Typography variant="body1">
                                  {questionAnalysis.question_text}
                                </Typography>
                              </Box>
                            )}

                            {/* Analysis */}
                            {questionAnalysis?.analysis && (
                              <Box mb={3}>
                                <Typography
                                  variant="body2"
                                  fontWeight="medium"
                                  gutterBottom
                                >
                                  Analysis
                                </Typography>
                                <Typography variant="body2">
                                  {questionAnalysis.analysis}
                                </Typography>
                              </Box>
                            )}

                            {/* Key Points */}
                            {questionAnalysis?.key_points &&
                              questionAnalysis.key_points.length > 0 && (
                                <Box mb={3}>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    gutterBottom
                                  >
                                    Key Points
                                  </Typography>
                                  <List dense>
                                    {questionAnalysis.key_points.map(
                                      (point: string, pIndex: number) => (
                                        <ListItem key={pIndex}>
                                          <ListItemIcon sx={{ minWidth: 30 }}>
                                            <Typography variant="caption">
                                              •
                                            </Typography>
                                          </ListItemIcon>
                                          <ListItemText
                                            primary={point}
                                            primaryTypographyProps={{
                                              variant: "body2",
                                            }}
                                          />
                                        </ListItem>
                                      )
                                    )}
                                  </List>
                                </Box>
                              )}

                            {/* Follow-up Questions Asked */}
                            {questionAnalysis?.follow_up_questions_asked &&
                              questionAnalysis.follow_up_questions_asked
                                .length > 0 && (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    gutterBottom
                                  >
                                    Follow-up Questions Asked
                                  </Typography>
                                  <List dense>
                                    {questionAnalysis.follow_up_questions_asked.map(
                                      (question: string, qIndex: number) => (
                                        <ListItem key={qIndex}>
                                          <ListItemIcon sx={{ minWidth: 30 }}>
                                            <Typography variant="caption">
                                              •
                                            </Typography>
                                          </ListItemIcon>
                                          <ListItemText
                                            primary={question}
                                            primaryTypographyProps={{
                                              variant: "body2",
                                            }}
                                          />
                                        </ListItem>
                                      )
                                    )}
                                  </List>
                                </Box>
                              )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    }
                  )}
                </Paper>
              </Grid>
            )}

          {/* Raw Analysis Results (for debugging) */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" color="text.secondary">
                  Raw Analysis Results (Debug)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <pre style={{ overflow: "auto", fontSize: "0.75rem" }}>
                  {JSON.stringify(analysisResults, null, 2)}
                </pre>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </>
      ) : (
        <Grid item xs={12}>
          <Alert severity="info">
            {currentAssessedSession.state === "processing"
              ? "This assessment session is currently being processed. Please check back later for results."
              : currentAssessedSession.state === "active"
              ? "This assessment session is currently active."
              : currentAssessedSession.state === "created"
              ? "This assessment session has been created but not yet started."
              : "No analysis results available for this session."}
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

export default AssessedSessionMemoryView;
